/**
 * M11 — Riportok / Kimutatások oldal
 *
 * alap_riport (standard+): Árbevétel, Kintlévőség összesítő, Top partnerek
 * teljes_riport (pro+):    FIFO haszon, Ajánlat-konverzió, Projekt-jövedelmezőség, Pénztár
 *
 * Időszak-szűrő + CSV export minden riporthoz.
 */
import { useState, useEffect, useCallback } from 'react';
import { get } from '../../lib/api';
import { PageHeader, Card, Button, Badge } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import { useFeatureContext } from '../../contexts/FeatureContext';

// ── Típusok ───────────────────────────────────────────────────────────────────

interface RevenueRow { period: string; net_huf: number; gross_huf: number; invoice_count: number }
interface TopPartnerRow { partner_id: string; partner_name: string; invoice_count: number; net_huf: number; gross_huf: number; paid_huf: number }
interface ReceivablesBand { band: string; invoice_count: number; outstanding_huf: number }
interface ProfitRow { period: string; revenue_net_huf: number; cost_net_huf: number; profit_huf: number; margin_pct: number }
interface QuoteConversion { total_quotes: number; converted_quotes: number; declined_quotes: number; expired_quotes: number; conversion_rate_pct: number; avg_value_huf: number; converted_value_huf: number }
interface ProjectRow { project_id: string; project_name: string; partner_name: string | null; billed_huf: number; time_cost_huf: number; profit_huf: number; total_hours: number }
interface CashSummary { bpb_count: number; bpb_total_huf: number; kpb_count: number; kpb_total_huf: number; net_huf: number; daily: Array<{ date: string; bpb_huf: number; kpb_huf: number; net_huf: number }> }

// ── Segédek ───────────────────────────────────────────────────────────────────

function fmtHuf(v: number) {
  return v.toLocaleString('hu-HU', { style: 'currency', currency: 'HUF', maximumFractionDigits: 0 });
}
function fmtNum(v: number) { return v.toLocaleString('hu-HU'); }

function defaultFrom() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() - 2, 1).toISOString().slice(0, 10);
}
function defaultTo() { return new Date().toISOString().slice(0, 10); }

function csvExportUrl(path: string, from: string, to: string, extra?: string) {
  return `/api${path}?from=${from}&to=${to}&format=csv${extra ? '&' + extra : ''}`;
}

function CsvButton({ url, label }: { url: string; label?: string }) {
  return (
    <a href={url} download style={{ textDecoration: 'none' }}>
      <Button size="sm" variant="secondary">⬇ {label ?? 'CSV'}</Button>
    </a>
  );
}

// ── Dátum szűrő ──────────────────────────────────────────────────────────────

interface DateRangeProps {
  from: string; to: string;
  onFrom: (v: string) => void; onTo: (v: string) => void;
}
function DateRangeFilter({ from, to, onFrom, onTo }: DateRangeProps) {
  const inputStyle: React.CSSProperties = {
    padding: '7px 10px', borderRadius: 7, border: '1px solid var(--border)',
    fontSize: 13, background: 'var(--input-bg)',
  };
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600 }}>Időszak:</span>
      <input type="date" value={from} onChange={e => onFrom(e.target.value)} style={inputStyle} />
      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>—</span>
      <input type="date" value={to} onChange={e => onTo(e.target.value)} style={inputStyle} />
    </div>
  );
}

// ── Árbevétel riport ──────────────────────────────────────────────────────────

function RevenueReport() {
  const { toast } = useToast();
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [groupBy, setGroupBy] = useState<'month' | 'week' | 'day'>('month');
  const [data, setData] = useState<RevenueRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await get<RevenueRow[]>(`/reports/revenue?from=${from}&to=${to}&group_by=${groupBy}`);
      setData(rows);
    } catch { toast('Betöltési hiba', 'error'); }
    finally { setLoading(false); }
  }, [from, to, groupBy]);

  useEffect(() => { load(); }, [load]);

  const total = data.reduce((s, r) => s + r.net_huf, 0);

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <DateRangeFilter from={from} to={to} onFrom={setFrom} onTo={setTo} />
        <select value={groupBy} onChange={e => setGroupBy(e.target.value as typeof groupBy)}
          style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid var(--border)', fontSize: 13 }}>
          <option value="month">Havi</option>
          <option value="week">Heti</option>
          <option value="day">Napi</option>
        </select>
        <CsvButton url={csvExportUrl('/reports/revenue', from, to, `group_by=${groupBy}`)} />
      </div>

      <div style={{ marginBottom: 16 }}>
        <Card style={{ padding: '12px 16px', display: 'inline-block' }}>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase' }}>Összes nettó</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--success)' }}>{fmtHuf(total)}</div>
        </Card>
      </div>

      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>Betöltés...</div>
      ) : data.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>Nincs adat a megadott időszakban.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                {['Időszak', 'Nettó', 'Bruttó', 'Számlák száma'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '7px 10px', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map(r => (
                <tr key={r.period} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '7px 10px', fontWeight: 600 }}>{r.period}</td>
                  <td style={{ padding: '7px 10px', color: 'var(--success)', fontWeight: 600 }}>{fmtHuf(r.net_huf)}</td>
                  <td style={{ padding: '7px 10px' }}>{fmtHuf(r.gross_huf)}</td>
                  <td style={{ padding: '7px 10px', textAlign: 'center' }}>{r.invoice_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Top partnerek ─────────────────────────────────────────────────────────────

function TopPartnersReport() {
  const { toast } = useToast();
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [data, setData] = useState<TopPartnerRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await get<TopPartnerRow[]>(`/reports/top-partners?from=${from}&to=${to}&limit=20`));
    } catch { toast('Betöltési hiba', 'error'); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <DateRangeFilter from={from} to={to} onFrom={setFrom} onTo={setTo} />
        <CsvButton url={csvExportUrl('/reports/top-partners', from, to)} />
      </div>
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>Betöltés...</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                {['#', 'Partner', 'Számlák', 'Nettó', 'Bruttó', 'Befizetve'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '7px 10px', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((r, i) => (
                <tr key={r.partner_id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '7px 10px', color: 'var(--text-secondary)' }}>{i + 1}</td>
                  <td style={{ padding: '7px 10px', fontWeight: 600 }}>{r.partner_name}</td>
                  <td style={{ padding: '7px 10px', textAlign: 'center' }}>{r.invoice_count}</td>
                  <td style={{ padding: '7px 10px', fontWeight: 600, color: 'var(--success)' }}>{fmtHuf(r.net_huf)}</td>
                  <td style={{ padding: '7px 10px' }}>{fmtHuf(r.gross_huf)}</td>
                  <td style={{ padding: '7px 10px' }}>{fmtHuf(r.paid_huf)}</td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>Nincs adat.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Kintlévőség összesítő ─────────────────────────────────────────────────────

const BAND_LABEL: Record<string, string> = {
  'not_due': 'Még nem lejárt',
  '1-30':   '1–30 nap',
  '31-60':  '31–60 nap',
  '61-90':  '61–90 nap',
  '91+':    '91+ nap',
};
const BAND_COLOR: Record<string, string> = {
  'not_due': 'var(--success)',
  '1-30':   'var(--warning)',
  '31-60':  'orange',
  '61-90':  'var(--error)',
  '91+':    'var(--error)',
};

function ReceivablesSummaryReport() {
  const { toast } = useToast();
  const [data, setData] = useState<ReceivablesBand[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    get<ReceivablesBand[]>('/reports/receivables-summary')
      .then(setData)
      .catch(() => toast('Betöltési hiba', 'error'))
      .finally(() => setLoading(false));
  }, []);

  const total = data.reduce((s, r) => s + r.outstanding_huf, 0);

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <CsvButton url="/api/reports/receivables-summary?format=csv" label="CSV export" />
      </div>
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>Betöltés...</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 20 }}>
            {data.map(r => (
              <Card key={r.band} style={{ padding: '12px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>{BAND_LABEL[r.band]}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: BAND_COLOR[r.band] ?? 'var(--text)' }}>{fmtHuf(r.outstanding_huf)}</div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{r.invoice_count} db számla</div>
              </Card>
            ))}
          </div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>
            Összes kintlévőség: <span style={{ color: 'var(--error)' }}>{fmtHuf(total)}</span>
          </div>
        </>
      )}
    </div>
  );
}

// ── FIFO haszon ───────────────────────────────────────────────────────────────

function ProfitFifoReport() {
  const { toast } = useToast();
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [groupBy, setGroupBy] = useState<'month' | 'week' | 'day'>('month');
  const [data, setData] = useState<ProfitRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await get<ProfitRow[]>(`/reports/profit-fifo?from=${from}&to=${to}&group_by=${groupBy}`));
    } catch { toast('Betöltési hiba', 'error'); }
    finally { setLoading(false); }
  }, [from, to, groupBy]);

  useEffect(() => { load(); }, [load]);

  const totalProfit = data.reduce((s, r) => s + r.profit_huf, 0);

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <DateRangeFilter from={from} to={to} onFrom={setFrom} onTo={setTo} />
        <select value={groupBy} onChange={e => setGroupBy(e.target.value as typeof groupBy)}
          style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid var(--border)', fontSize: 13 }}>
          <option value="month">Havi</option>
          <option value="week">Heti</option>
          <option value="day">Napi</option>
        </select>
        <CsvButton url={csvExportUrl('/reports/profit-fifo', from, to, `group_by=${groupBy}`)} />
      </div>

      <div style={{ marginBottom: 16 }}>
        <Card style={{ padding: '12px 16px', display: 'inline-block' }}>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase' }}>Összes nyereség</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: totalProfit >= 0 ? 'var(--success)' : 'var(--error)' }}>{fmtHuf(totalProfit)}</div>
        </Card>
      </div>

      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>Betöltés...</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                {['Időszak', 'Bevétel', 'Kiadás', 'Nyereség', 'Marzs'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '7px 10px', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map(r => (
                <tr key={r.period} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '7px 10px', fontWeight: 600 }}>{r.period}</td>
                  <td style={{ padding: '7px 10px', color: 'var(--success)' }}>{fmtHuf(r.revenue_net_huf)}</td>
                  <td style={{ padding: '7px 10px', color: 'var(--error)' }}>{fmtHuf(r.cost_net_huf)}</td>
                  <td style={{ padding: '7px 10px', fontWeight: 700, color: r.profit_huf >= 0 ? 'var(--success)' : 'var(--error)' }}>{fmtHuf(r.profit_huf)}</td>
                  <td style={{ padding: '7px 10px' }}>{r.margin_pct}%</td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>Nincs adat.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Ajánlat-konverzió ─────────────────────────────────────────────────────────

function QuoteConversionReport() {
  const { toast } = useToast();
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [data, setData] = useState<QuoteConversion | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await get<QuoteConversion>(`/reports/quote-conversion?from=${from}&to=${to}`));
    } catch { toast('Betöltési hiba', 'error'); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <DateRangeFilter from={from} to={to} onFrom={setFrom} onTo={setTo} />
        <CsvButton url={csvExportUrl('/reports/quote-conversion', from, to)} />
      </div>
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>Betöltés...</div>
      ) : data ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
          {[
            { label: 'Összes ajánlat', value: fmtNum(data.total_quotes), color: 'var(--text)' },
            { label: 'Elfogadva', value: fmtNum(data.converted_quotes), color: 'var(--success)' },
            { label: 'Elutasítva', value: fmtNum(data.declined_quotes), color: 'var(--error)' },
            { label: 'Lejárt', value: fmtNum(data.expired_quotes), color: 'var(--warning)' },
            { label: 'Konverzió', value: `${data.conversion_rate_pct}%`, color: 'var(--navy, #1e3a5f)' },
            { label: 'Átl. érték', value: fmtHuf(data.avg_value_huf), color: 'var(--text-secondary)' },
            { label: 'Elfog. érték', value: fmtHuf(data.converted_value_huf), color: 'var(--success)' },
          ].map(c => (
            <Card key={c.label} style={{ padding: '12px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600, marginBottom: 4 }}>{c.label}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: c.color }}>{c.value}</div>
            </Card>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ── Projekt-jövedelmezőség ────────────────────────────────────────────────────

function ProjectProfitabilityReport() {
  const { toast } = useToast();
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [data, setData] = useState<ProjectRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await get<ProjectRow[]>(`/reports/project-profitability?from=${from}&to=${to}`));
    } catch { toast('Betöltési hiba', 'error'); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <DateRangeFilter from={from} to={to} onFrom={setFrom} onTo={setTo} />
        <CsvButton url={csvExportUrl('/reports/project-profitability', from, to)} />
      </div>
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>Betöltés...</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                {['Projekt', 'Partner', 'Számlázva', 'Munkaidő-ktg', 'Nyereség', 'Óra'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '7px 10px', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map(r => (
                <tr key={r.project_id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '7px 10px', fontWeight: 600 }}>{r.project_name}</td>
                  <td style={{ padding: '7px 10px', color: 'var(--text-secondary)' }}>{r.partner_name ?? '—'}</td>
                  <td style={{ padding: '7px 10px' }}>{fmtHuf(r.billed_huf)}</td>
                  <td style={{ padding: '7px 10px', color: 'var(--error)' }}>{fmtHuf(r.time_cost_huf)}</td>
                  <td style={{ padding: '7px 10px', fontWeight: 700, color: r.profit_huf >= 0 ? 'var(--success)' : 'var(--error)' }}>{fmtHuf(r.profit_huf)}</td>
                  <td style={{ padding: '7px 10px', textAlign: 'right' }}>{r.total_hours} h</td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>Nincs adat.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Pénztár összesítő ─────────────────────────────────────────────────────────

function CashSummaryReport() {
  const { toast } = useToast();
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [data, setData] = useState<CashSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await get<CashSummary>(`/reports/cash-summary?from=${from}&to=${to}`));
    } catch { toast('Betöltési hiba', 'error'); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <DateRangeFilter from={from} to={to} onFrom={setFrom} onTo={setTo} />
        <CsvButton url={csvExportUrl('/reports/cash-summary', from, to)} />
      </div>
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>Betöltés...</div>
      ) : data ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 20 }}>
            <Card style={{ padding: '12px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600 }}>BPB (bevétel)</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--success)' }}>{fmtHuf(data.bpb_total_huf)}</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{data.bpb_count} bizonylat</div>
            </Card>
            <Card style={{ padding: '12px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600 }}>KPB (kiadás)</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--error)' }}>{fmtHuf(data.kpb_total_huf)}</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{data.kpb_count} bizonylat</div>
            </Card>
            <Card style={{ padding: '12px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600 }}>Nettó egyenleg</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: data.net_huf >= 0 ? 'var(--success)' : 'var(--error)' }}>{fmtHuf(data.net_huf)}</div>
            </Card>
          </div>

          {data.daily.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)' }}>
                    {['Dátum', 'BPB', 'KPB', 'Nettó'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '7px 10px', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.daily.map(d => (
                    <tr key={d.date} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '7px 10px', fontWeight: 600 }}>{d.date}</td>
                      <td style={{ padding: '7px 10px', color: 'var(--success)' }}>{fmtHuf(d.bpb_huf)}</td>
                      <td style={{ padding: '7px 10px', color: 'var(--error)' }}>{fmtHuf(d.kpb_huf)}</td>
                      <td style={{ padding: '7px 10px', fontWeight: 700, color: d.net_huf >= 0 ? 'var(--success)' : 'var(--error)' }}>{fmtHuf(d.net_huf)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

// ── Upgrade CTA ───────────────────────────────────────────────────────────────

function UpgradeCta({ requiredPlan }: { requiredPlan: 'standard' | 'pro' }) {
  return (
    <div style={{ padding: '32px 0', textAlign: 'center' }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>
        Ez a riport {requiredPlan === 'pro' ? 'Pro' : 'Standard'} csomagban érhető el
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
        Frissítsd előfizetésedet a teljes riport-csomag eléréséhez.
      </div>
      <a href="/beallitasok/szamlazas">
        <Button variant="primary">Csomag frissítése</Button>
      </a>
    </div>
  );
}

// ── Fő oldal ──────────────────────────────────────────────────────────────────

type TabId = 'revenue' | 'top-partners' | 'receivables' | 'profit-fifo' | 'quote-conv' | 'project-prof' | 'cash';

interface TabDef {
  id: TabId;
  label: string;
  requiredFeature: 'alap_riport' | 'teljes_riport';
}

const TABS: TabDef[] = [
  { id: 'revenue',      label: 'Árbevétel',             requiredFeature: 'alap_riport'  },
  { id: 'top-partners', label: 'Top partnerek',          requiredFeature: 'alap_riport'  },
  { id: 'receivables',  label: 'Kintlévőség',            requiredFeature: 'alap_riport'  },
  { id: 'profit-fifo',  label: 'FIFO haszon',            requiredFeature: 'teljes_riport' },
  { id: 'quote-conv',   label: 'Ajánlat-konverzió',      requiredFeature: 'teljes_riport' },
  { id: 'project-prof', label: 'Projekt-jövedelmezőség', requiredFeature: 'teljes_riport' },
  { id: 'cash',         label: 'Pénztár',                requiredFeature: 'teljes_riport' },
];

export default function ReportsPage() {
  const { activeFeatures } = useFeatureContext();
  const [tab, setTab] = useState<TabId>('revenue');

  const hasAlap = activeFeatures.includes('alap_riport');
  const hasTeljes = activeFeatures.includes('teljes_riport');

  if (!hasAlap) {
    return (
      <div>
        <PageHeader title="Riportok" subtitle="Pénzügyi kimutatások és elemzések" />
        <UpgradeCta requiredPlan="standard" />
      </div>
    );
  }

  const currentTab = TABS.find(t => t.id === tab)!;
  const tabBlocked = currentTab.requiredFeature === 'teljes_riport' && !hasTeljes;

  return (
    <div>
      <PageHeader title="Riportok" subtitle="Pénzügyi kimutatások és elemzések" />

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {TABS.map(t => {
          const locked = t.requiredFeature === 'teljes_riport' && !hasTeljes;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                padding: '6px 14px',
                borderRadius: 20,
                border: '1px solid var(--border)',
                background: tab === t.id ? 'var(--primary)' : 'var(--paper)',
                color: tab === t.id ? '#fff' : locked ? 'var(--text-secondary)' : 'var(--text)',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                opacity: locked ? 0.6 : 1,
              }}
            >
              {t.label} {locked && '🔒'}
            </button>
          );
        })}
      </div>

      <Card style={{ padding: '20px 24px' }}>
        {tabBlocked ? (
          <UpgradeCta requiredPlan="pro" />
        ) : tab === 'revenue' ? <RevenueReport />
          : tab === 'top-partners' ? <TopPartnersReport />
          : tab === 'receivables' ? <ReceivablesSummaryReport />
          : tab === 'profit-fifo' ? <ProfitFifoReport />
          : tab === 'quote-conv' ? <QuoteConversionReport />
          : tab === 'project-prof' ? <ProjectProfitabilityReport />
          : tab === 'cash' ? <CashSummaryReport />
          : null}
      </Card>
    </div>
  );
}
